import { useState, useRef, useLayoutEffect, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Filter, X, Plus } from 'lucide-react'
import type { CrudFilter, LogicalFilter } from '@refinedev/core'
import { computeFilterPanelRect } from '@/components/shared/filterPanelPosition'

const OPERATORS = [
  { value: 'contains',   label: 'contains' },
  { value: 'eq',         label: 'equals' },
  { value: 'startswith', label: 'starts with' },
  { value: 'endswith',   label: 'ends with' },
  { value: 'gt',         label: '>' },
  { value: 'lt',         label: '<' },
] as const

const PANEL_Z = 50

type Operator = typeof OPERATORS[number]['value']

interface FilterRow {
  field: string
  operator: Operator
  value: string
}

interface FilterPanelProps {
  columns: string[]
  activeFilters: CrudFilter[]
  onApply: (filters: LogicalFilter[]) => void
  onReset: () => void
}

export function FilterPanel({ columns, activeFilters, onApply, onReset }: FilterPanelProps) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<FilterRow[]>(
    activeFilters
      .filter((f): f is LogicalFilter => 'field' in f)
      .map((f) => ({
        field: (f as LogicalFilter).field,
        operator: ((f as LogicalFilter).operator as Operator) ?? 'contains',
        value: String((f as LogicalFilter).value ?? ''),
      }))
  )
  const triggerWrapRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelRect, setPanelRect] = useState<{ top: number; left: number; width: number } | null>(null)

  const addRow = () =>
    setRows([...rows, { field: columns[0] ?? '', operator: 'contains', value: '' }])

  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i))

  const updateRow = (i: number, patch: Partial<FilterRow>) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  const handleApply = () => {
    onApply(
      rows
        .filter((r) => r.field && r.value)
        .map((r) => ({ field: r.field, operator: r.operator, value: r.value }))
    )
    setOpen(false)
  }

  const handleReset = () => {
    setRows([])
    onReset()
    setOpen(false)
  }

  const activeCount = activeFilters.filter(
    (f): f is LogicalFilter => 'field' in f && !!(f as LogicalFilter).value
  ).length

  const measure = () => {
    const el = triggerWrapRef.current
    if (!el) return
    setPanelRect(
      computeFilterPanelRect(el.getBoundingClientRect(), window.innerWidth)
    )
  }

  useLayoutEffect(() => {
    if (!open) {
      setPanelRect(null)
      return
    }
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (triggerWrapRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const panelContent = open && panelRect && (
    <div
      ref={panelRef}
      className="fixed glass-card rounded-2xl border border-white/70 dark:border-white/10 shadow-xl p-4 space-y-3"
      style={{
        top: panelRect.top,
        left: panelRect.left,
        width: panelRect.width,
        zIndex: PANEL_Z,
      }}
    >
      {rows.length === 0 && (
        <p className="text-xs text-muted-foreground">No filters. Add one below.</p>
      )}
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select value={row.field} onValueChange={(v) => updateRow(i, { field: v ?? '' })}>
            <SelectTrigger className="h-8 text-xs flex-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {columns.map((c) => (
                <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={row.operator}
            onValueChange={(v) => updateRow(i, { operator: (v ?? 'contains') as Operator })}
          >
            <SelectTrigger className="h-8 text-xs w-24 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPERATORS.map((op) => (
                <SelectItem key={op.value} value={op.value} className="text-xs">
                  {op.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className="h-8 text-xs flex-1"
            placeholder="value"
            value={row.value}
            onChange={(e) => updateRow(i, { value: e.target.value })}
          />
          <Button
            variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0"
            onClick={() => removeRow(i)}
          >
            <X size={13} />
          </Button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-[#076698] hover:underline"
      >
        <Plus size={12} /> Add filter
      </button>
      <div className="flex gap-2 pt-2 border-t border-border/30 dark:border-white/10">
        <Button size="sm" className="bg-[#076698] hover:bg-[#055580]" onClick={handleApply}>
          Apply
        </Button>
        <Button size="sm" variant="outline" onClick={handleReset}>
          Reset
        </Button>
      </div>
    </div>
  )

  return (
    <>
      <div ref={triggerWrapRef} className="inline-flex shrink-0">
        <Button variant="outline" size="sm" onClick={() => setOpen(!open)} className="gap-1.5">
          <Filter size={13} />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 bg-[#076698] text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none">
              {activeCount}
            </span>
          )}
        </Button>
      </div>
      {panelContent && createPortal(panelContent, document.body)}
    </>
  )
}
